import { Caption } from '@remotion/captions';
import { SetState } from '@/features/remotion-editor/editor/context-provider';
import { isCaptionAsset } from '@/features/remotion-editor/editor/assets/assets';
import { isCaptionsItem } from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { safeMs, ensureEndGreaterThanStart, computeMidpoint } from './helpers';
import { calculateCaptionAssetSize } from './asset-utils';
import { syncEditedCaptionsToStore } from '@/features/remotion-editor/editor/state/actions/caption-translation-actions';

/**
 * 동일 asset 안에서 단어를 두 개로 쪼갠다.
 * @param itemId 대상 captions item id
 * @param wordIndex 분할할 단어 index
 * @param splitOffset caret 위치(글자 index)
 * @param setState 전역 상태 업데이트 함수
 * @returns 분할이 성공하면 true
 */
export const splitCaptionWordWithinAsset = ({
  itemId,
  wordIndex,
  splitOffset,
  setState,
}: {
  itemId: string;
  wordIndex: number;
  splitOffset: number;
  setState: SetState;
}): boolean => {
  let didUpdate = false;

  // 동일 asset 내에서 caret 기준으로 단어를 두 개로 쪼개는 로직.
  setState({
    update: (prev) => {
      const { items, assets } = prev.undoableState;
      const currentItem = items[itemId];

      if (!isCaptionsItem(currentItem)) {
        return prev;
      }

      const asset = assets[currentItem.assetId];
      if (!isCaptionAsset(asset)) {
        return prev;
      }

      if (wordIndex < 0 || wordIndex >= asset.captions.length) {
        return prev;
      }

      const targetCaption = asset.captions[wordIndex];
      if (!targetCaption) {
        return prev;
      }

      const rawText = targetCaption.text ?? '';
      // 원본 텍스트(선행 공백 포함)에서 실제 단어 부분만 분리.
      const normalizedCore = rawText.trim();
      const totalLength = normalizedCore.length;

      if (totalLength < 2 || splitOffset <= 0 || splitOffset >= totalLength) {
        return prev;
      }

      const baseStart = safeMs(targetCaption.startMs);
      const baseEnd = ensureEndGreaterThanStart(
        baseStart,
        safeMs(targetCaption.endMs),
      );
      const totalDuration = baseEnd - baseStart;

      if (totalDuration < 2) {
        return prev;
      }

      const firstCore = normalizedCore.slice(0, splitOffset);
      const secondCore = normalizedCore.slice(splitOffset);

      if (!firstCore || !secondCore) {
        return prev;
      }

      const leadingSpace = rawText.match(/^\s+/)?.[0] ?? ' ';
      const trailingSpace = rawText.match(/\s*$/)?.[0] ?? '';

      // 글자 수 비율에 따라 앞/뒤 구간 시간을 나눈다.
      const proposedFirstEnd =
        baseStart +
        Math.round((totalDuration * firstCore.length) / totalLength);
      const firstEnd = Math.max(
        baseStart + 1,
        Math.min(baseEnd - 1, proposedFirstEnd),
      );
      const secondStart = firstEnd;
      const secondEnd = baseEnd;

      if (secondEnd <= secondStart) {
        return prev;
      }

      const firstCaption: Caption = {
        ...targetCaption,
        text: `${leadingSpace}${firstCore}`,
        startMs: baseStart,
        endMs: firstEnd,
        timestampMs: computeMidpoint(baseStart, firstEnd),
      };

      const secondCaption: Caption = {
        ...targetCaption,
        text: ` ${secondCore}${trailingSpace}`,
        startMs: secondStart,
        endMs: secondEnd,
        timestampMs: computeMidpoint(secondStart, secondEnd),
      };

      const nextCaptions = [
        ...asset.captions.slice(0, wordIndex),
        firstCaption,
        secondCaption,
        ...asset.captions.slice(wordIndex + 1),
      ];

      didUpdate = true;

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          assets: {
            ...prev.undoableState.assets,
            [asset.id]: {
              ...asset,
              captions: nextCaptions,
              size: calculateCaptionAssetSize(nextCaptions),
              // 번역 asset 이면 표시 중인 쪽 보관본도 같이 갱신 (design.md §7-4)
              ...syncEditedCaptionsToStore(asset, nextCaptions),
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });

  return didUpdate;
};

/**
 * 동일 asset 내 인접한 두 단어를 하나로 병합한다.
 * @param itemId 대상 captions item id
 * @param wordIndex 병합 기준 현재 단어 index
 * @param setState 전역 상태 업데이트 함수
 * @returns 병합이 일어나면 true
 */
export const mergeCaptionWordWithinAsset = ({
  itemId,
  wordIndex,
  setState,
}: {
  itemId: string;
  wordIndex: number;
  setState: SetState;
}): boolean => {
  let didUpdate = false;

  // 동일 asset 내에서 caret 앞 단어와 현재 단어를 하나로 묶는다.
  setState({
    update: (prev) => {
      const { items, assets } = prev.undoableState;
      const currentItem = items[itemId];

      if (!isCaptionsItem(currentItem)) {
        return prev;
      }

      const asset = assets[currentItem.assetId];
      if (!isCaptionAsset(asset)) {
        return prev;
      }

      if (wordIndex <= 0 || wordIndex >= asset.captions.length) {
        return prev;
      }

      const prevCaption = asset.captions[wordIndex - 1];
      const currentCaption = asset.captions[wordIndex];

      if (!prevCaption || !currentCaption) {
        return prev;
      }

      const prevCore = (prevCaption.text ?? '').trim();
      const currentCore = (currentCaption.text ?? '').trim();

      if (!prevCore || !currentCore) {
        return prev;
      }

      const leadingSpace =
        prevCaption.text?.match(/^\s+/)?.[0] ??
        currentCaption.text?.match(/^\s+/)?.[0] ??
        ' ';
      const trailingSpace = currentCaption.text?.match(/\s*$/)?.[0] ?? '';

      const prevStart = safeMs(prevCaption.startMs);
      const prevEnd = ensureEndGreaterThanStart(
        prevStart,
        safeMs(prevCaption.endMs),
      );
      const currentStart = safeMs(currentCaption.startMs);
      const currentEnd = ensureEndGreaterThanStart(
        currentStart,
        safeMs(currentCaption.endMs),
      );

      const mergedStart = Math.min(prevStart, currentStart);
      const mergedEnd = Math.max(prevEnd, currentEnd);

      if (mergedEnd <= mergedStart) {
        return prev;
      }

      const mergedCaption: Caption = {
        ...prevCaption,
        text: `${leadingSpace}${prevCore}${currentCore}${trailingSpace}`,
        startMs: mergedStart,
        endMs: mergedEnd,
        timestampMs: computeMidpoint(mergedStart, mergedEnd),
      };

      const nextCaptions = [
        ...asset.captions.slice(0, wordIndex - 1),
        mergedCaption,
        ...asset.captions.slice(wordIndex + 1),
      ];

      didUpdate = true;

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          assets: {
            ...prev.undoableState.assets,
            [asset.id]: {
              ...asset,
              captions: nextCaptions,
              size: calculateCaptionAssetSize(nextCaptions),
              // 번역 asset 이면 표시 중인 쪽 보관본도 같이 갱신 (design.md §7-4)
              ...syncEditedCaptionsToStore(asset, nextCaptions),
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });

  return didUpdate;
};

/**
 * 동일 asset 내 특정 단어에 newline(\n)을 삽입해 UI/데이터를 동기화한다.
 * @param itemId 대상 captions item id
 * @param wordIndex caret가 위치한 단어 index
 * @param position caret가 단어 앞인지 뒤인지(before|after)
 * @param setState 전역 에디터 상태 업데이트 함수
 * @returns asset이 갱신되면 true
 */
export const addCaptionWordLineBreakWithinAsset = ({
  itemId,
  wordIndex,
  position,
  setState,
}: {
  itemId: string;
  wordIndex: number;
  position: 'before' | 'after';
  setState: SetState;
}): boolean => {
  let didUpdate = false;

  setState({
    update: (prev) => {
      const { items, assets } = prev.undoableState;
      const currentItem = items[itemId];

      if (!isCaptionsItem(currentItem)) {
        return prev;
      }

      const asset = assets[currentItem.assetId];
      if (!isCaptionAsset(asset)) {
        return prev;
      }

      const targetIndex = position === 'before' ? wordIndex - 1 : wordIndex;

      if (targetIndex < 0 || targetIndex >= asset.captions.length) {
        return prev;
      }

      const targetCaption = asset.captions[targetIndex];
      if (!targetCaption) {
        return prev;
      }

      const originalText = targetCaption.text ?? '';
      // trailing 공백을 보존하면서 text 끝에 newline(\n)을 추가한다.
      const trailingSpacesMatch = originalText.match(/[ \t]+$/);
      const trailingSpaces = trailingSpacesMatch?.[0] ?? '';
      const coreText = trailingSpaces
        ? originalText.slice(0, originalText.length - trailingSpaces.length)
        : originalText;

      if (!coreText || coreText.endsWith('\n') || coreText.endsWith('\r')) {
        return prev;
      }

      const nextText = `${coreText}\n${trailingSpaces}`;
      const nextCaptions = asset.captions.map((caption, idx) =>
        idx === targetIndex
          ? {
              ...caption,
              text: nextText,
            }
          : caption,
      );

      didUpdate = true;

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          assets: {
            ...prev.undoableState.assets,
            [asset.id]: {
              ...asset,
              captions: nextCaptions,
              size: calculateCaptionAssetSize(nextCaptions),
              // 번역 asset 이면 표시 중인 쪽 보관본도 같이 갱신 (design.md §7-4)
              ...syncEditedCaptionsToStore(asset, nextCaptions),
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });

  return didUpdate;
};

/**
 * 개행된 단어를 다시 한 줄로 합칠 때 newline(\n)을 제거한다.
 * @param itemId 대상 captions item id
 * @param wordIndex caret가 위치한 단어 index
 * @param setState 전역 에디터 상태 업데이트 함수
 * @returns newline을 제거하고 asset을 갱신하면 true
 */
export const removeCaptionWordLineBreakWithinAsset = ({
  itemId,
  wordIndex,
  setState,
}: {
  itemId: string;
  wordIndex: number;
  setState: SetState;
}): boolean => {
  let didUpdate = false;

  setState({
    update: (prev) => {
      const { items, assets } = prev.undoableState;
      const currentItem = items[itemId];

      if (!isCaptionsItem(currentItem)) {
        return prev;
      }

      const asset = assets[currentItem.assetId];
      if (!isCaptionAsset(asset)) {
        return prev;
      }

      const targetIndex = wordIndex - 1;

      if (targetIndex < 0 || targetIndex >= asset.captions.length) {
        return prev;
      }

      const targetCaption = asset.captions[targetIndex];
      if (!targetCaption) {
        return prev;
      }

      const originalText = targetCaption.text ?? '';
      if (!/(?:\r?\n)+[ \t]*$/.test(originalText)) {
        return prev;
      }

      const nextText = originalText.replace(/(?:\r?\n)+([ \t]*)$/, '$1');
      const nextCaptions = asset.captions.map((caption, idx) =>
        idx === targetIndex
          ? {
              ...caption,
              text: nextText,
            }
          : caption,
      );

      didUpdate = true;

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          assets: {
            ...prev.undoableState.assets,
            [asset.id]: {
              ...asset,
              captions: nextCaptions,
              size: calculateCaptionAssetSize(nextCaptions),
              // 번역 asset 이면 표시 중인 쪽 보관본도 같이 갱신 (design.md §7-4)
              ...syncEditedCaptionsToStore(asset, nextCaptions),
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });

  return didUpdate;
};
